import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { dashboardAPI } from '../services/dashboardAPI';
import './AbueloDashboard.css';

export default function AbueloDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const data = await dashboardAPI.getAbueloStats(id, token);
        setStats(data);
      } catch (error) {
        console.error('Error:', error);
        setError('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, getAccessTokenSilently]);

  const getPromedioFelicidad = () => {
    if (!stats?.ultimoReporte?.estado_animo) return { valor: '--', sobre: 5 };
    const mapeo = { feliz: 5, neutral: 3, preocupado: 2, triste: 1, sin_datos: 0 };
    const valor = mapeo[stats.ultimoReporte.estado_animo] || 3;
    return { valor: valor.toFixed(1), sobre: 5 };
  };

  const emocionesConfig = {
    joy: { label: 'Feliz', color: 'bg-green-100 text-green-700', borderColor: '#86efac' },
    sadness: { label: 'Triste', color: 'bg-blue-100 text-blue-700', borderColor: '#93c5fd' },
    anger: { label: 'Enfadado', color: 'bg-red-100 text-red-700', borderColor: '#fca5a5' },
    fear: { label: 'Preocupado', color: 'bg-yellow-100 text-yellow-700', borderColor: '#fde047' },
    surprise: { label: 'Sorprendido', color: 'bg-purple-100 text-purple-700', borderColor: '#c4b5fd' },
    disgust: { label: 'Disgustado', color: 'bg-orange-100 text-orange-700', borderColor: '#fdba74' },
    neutral: { label: 'Neutral', color: 'bg-gray-100 text-gray-700', borderColor: '#d1d5db' }
  };

  if (loading) {
    return (
      <div className="abd-loading">
        <div className="abd-loading-spinner"></div>
        <p>Cargando reporte de bienestar...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="abd-loading">
        <div className="abd-loading-error">!</div>
        <p>{error || 'No se encontraron datos'}</p>
        <button onClick={() => navigate('/dashboard')} className="abd-btn-back">
          Volver al Dashboard
        </button>
      </div>
    );
  }

  const promedio = getPromedioFelicidad();
  const reporte = stats.ultimoReporte;

  return (
    <div className="abd-screen">
      <div className="abd-container">

        <div className="abd-top-actions">
          <button onClick={() => navigate('/dashboard')} className="abd-btn-volver">
            <span className="abd-arrow">←</span> Volver a mis familiares
          </button>
          <button
            onClick={async () => {
              try {
                const token = await getAccessTokenSilently();
                sessionStorage.setItem('paquita_familiar_token', token);
              } catch (_) { /* navegar igualmente; MonederoScreen mostrará el 401 */ }
              window.location.href = `/familiar/monedero?abueloId=${id}`;
            }}
            className="abd-btn-monedero"
          >
            💰 Monedero
          </button>
        </div>

        <div className="abd-header">
          <div className="abd-header-content">
            <img
              src={stats.foto_url || (parseInt(id) % 2 === 0 ? '/paquito.png' : '/paquita.jpg')}
              alt={stats.nombre}
              className="abd-avatar"
            />
            <div className="abd-header-info">
              <h1>{stats.nombre} {stats.apellidos || ''}</h1>
              <div className="abd-header-meta">
                <span className="abd-meta-item">
                  <span className="abd-meta-icon">📱</span>
                  {stats.telefono}
                </span>
                <span className="abd-meta-item">
                  <span className="abd-meta-icon">💳</span>
                  {stats.plan}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="abd-metrics-grid">
          <div className="abd-metric-card">
            <div className="abd-metric-icon blue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <div className="abd-metric-label">Total Conversaciones</div>
            <div className="abd-metric-value blue">{stats.totalConversaciones || 0}</div>
            <div className="abd-metric-sublabel">Todas las conversaciones</div>
          </div>

          <div className="abd-metric-card">
            <div className="abd-metric-icon green">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="abd-metric-label">Tiempo Total</div>
            <div className="abd-metric-value green">{stats.minutosTotales || 0} min</div>
            <div className="abd-metric-sublabel">Todas las conversaciones</div>
          </div>

          <div className="abd-metric-card">
            <div className="abd-metric-icon emerald">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </div>
            <div className="abd-metric-label">Estado de Ánimo</div>
            <div className="abd-metric-value emerald">{promedio.valor}/{promedio.sobre}</div>
            <div className="abd-metric-sublabel">
              {reporte?.estado_animo ? `Hoy: ${reporte.estado_animo}` : 'Sin datos aún'}
            </div>
          </div>

          <div className="abd-metric-card">
            <div className="abd-metric-icon purple">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="abd-metric-label">Indicador Soledad</div>
            <div className="abd-metric-value purple">{reporte?.indicador_soledad || 0}%</div>
            <div className="abd-metric-sublabel">
              {reporte?.indicador_soledad > 40 ? '⚠️ Requiere atención' : '✅ Nivel normal'}
            </div>
          </div>
        </div>

        <div className="abd-analysis-card">
          <div className="abd-analysis-header">
            <div className="abd-analysis-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h2>Resumen de Bienestar</h2>
              <p>Análisis emocional basado en las conversaciones con Paquita</p>
            </div>
          </div>

          {!reporte && stats.totalConversaciones === 0 ? (
            <div className="abd-empty-state">
              <div className="abd-empty-icon">👋</div>
              <h3>Aún no hay conversaciones con Paquita</h3>
              <p>Las estadísticas aparecerán después de la primera llamada</p>
            </div>
          ) : (
            <>
              {reporte?.temas && reporte.temas.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginTop: '16px' }}>
                  {reporte.temas.map((emotion, index) => {
                    const config = emocionesConfig[emotion] || emocionesConfig.neutral;
                    return (
                      <div key={index} style={{
                        padding: '20px 16px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        border: `2px solid ${config.borderColor}`,
                        backgroundColor: emotion === 'joy' ? '#f0fdf4' : emotion === 'neutral' ? '#f9fafb' : '#fefce8'
                      }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                          {emotion === 'joy' ? '😊' : emotion === 'neutral' ? '😐' : emotion === 'sadness' ? '😢' : emotion === 'fear' ? '😟' : '😶'}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{config.label}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="abd-empty-state" style={{ padding: '20px', textAlign: 'center' }}>
                  <p>El análisis emocional se generará a las 20:00</p>
                </div>
              )}

              {reporte?.recomendaciones && reporte.recomendaciones.length > 0 && (
                <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#166534' }}>💡 Recomendaciones</h3>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {reporte.recomendaciones.map((rec, i) => (
                      <li key={i} style={{ color: '#15803d', marginBottom: '4px' }}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="abd-activity-card">
          <h2>Actividad Reciente</h2>
          {stats.totalConversaciones === 0 ? (
            <div className="abd-empty-state small">
              <p>No hay actividad registrada todavía</p>
            </div>
          ) : (
            <div className="abd-table-wrapper">
              <table className="abd-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Duración</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{stats.ultimaActividad || 'Sin fecha'}</td>
                    <td>
                      <span className="abd-type-badge">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        Conversación
                      </span>
                    </td>
                    <td>{stats.minutosTotales || 1} min</td>
                    <td><span className="abd-status-badge success">Analizado</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="abd-footer">
          <p>Un servicio de cuidado proporcionado por <strong>Paquita.ai</strong></p>
        </div>

      </div>
    </div>
  );
}
